import { useMutation } from '@apollo/client'
import { CircleCheck, CircleMinus, Loader } from 'lucide-react'
import { useTransition } from 'react'
import { useMatch } from 'react-router'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H3 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { DisableSsoDialog } from '@/routes/single-sign-on/sso-config/disable-sso-dialog'
import type {
  CompanySamlSettingQuery,
  CompanySamlSettingQueryVariables,
  CreateSamlSettingMutation,
  CreateSamlSettingMutationVariables,
} from '@/routes/single-sign-on/sso-config/index.types.gen'

export const COMPANY_SAML_SETTING_QUERY = gql`
  query CompanySamlSetting($idCompany: String!) {
    company(id: $idCompany) {
      id
      companyOwner: users(idRole: "company-owner") {
        user {
          id
          firstName
          lastName
          capabilities {
            id
            isSamlExempt
          }
        }
      }
      users {
        id
        capabilities @live {
          id
          isSamlExempt
        }
        user {
          id
          email
          firstName
          lastName
        }
      }
      samlSetting @live {
        id
        domain
        isForced
        isEnabled
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
      draftSamlSetting: samlSetting(isDraft: true) @live {
        id
        isForced
        isJitEnabled
        domain
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
    }
  }
`

const CREATE_SAML_SETTING_MUTATION = gql`
  mutation CreateSamlSetting(
    $samlSetting: SamlSettingInput!
    $shouldSaveSilently: Boolean
  ) {
    saveSamlSetting(
      samlSetting: $samlSetting
      shouldSaveSilently: $shouldSaveSilently
    ) {
      id
    }
  }
`

const SsoStatusIndicator = ({
  isEnabled,
  domain,
}: {
  isEnabled: boolean
  domain?: string
}) => {
  return (
    <div className="flex items-center gap-2">
      {isEnabled ? (
        <CircleCheck className="size-4 fill-emerald-600 stroke-background" />
      ) : (
        <CircleMinus className="size-4 stroke-background fill-muted-foreground" />
      )}
      <span
        className={cn(
          'text-sm',
          isEnabled
            ? 'text-green-600 dark:text-green-400'
            : 'text-muted-foreground'
        )}
      >
        {isEnabled ? (
          <>SSO is enabled {domain && `• ${domain}`}</>
        ) : (
          <>SSO is disabled {domain && `• ${domain}`}</>
        )}
      </span>
    </div>
  )
}

export const SsoConfig = () => {
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const [isPending, startTransition] = useTransition()
  const isDisableDialogOpen = Boolean(useMatch('/single-sign-on/disable'))

  const { data } = useSuspenseQuery<
    CompanySamlSettingQuery,
    CompanySamlSettingQueryVariables
  >(COMPANY_SAML_SETTING_QUERY, {
    variables: { idCompany },
  })

  const [createSamlSetting] = useMutation<
    CreateSamlSettingMutation,
    CreateSamlSettingMutationVariables
  >(CREATE_SAML_SETTING_MUTATION)

  const liveSamlSetting = data?.company?.samlSetting
  const draftSamlSetting = data?.company?.draftSamlSetting
  const isSamlEnabled = liveSamlSetting?.isEnabled ?? false
  const samlDomain = liveSamlSetting?.domain ?? draftSamlSetting?.domain ?? ''
  const idSamlSetting = liveSamlSetting?.id ?? ''
  const idDraftSamlSetting = draftSamlSetting?.id ?? ''

  const openSamlSettingsForm = async () => {
    if (idSamlSetting || idDraftSamlSetting) {
      navigateToPath({
        pathname: '/single-sign-on/configure/:idSamlSetting',
        variables: { idSamlSetting: idSamlSetting || idDraftSamlSetting },
      })
    } else {
      startTransition(async () => {
        try {
          const { data } = await createSamlSetting({
            variables: {
              samlSetting: { idCompany },
              shouldSaveSilently: true,
            },
          })

          const id = data?.saveSamlSetting?.id
          if (id) {
            navigateToPath({
              pathname: '/single-sign-on/configure/:idSamlSetting',
              variables: { idSamlSetting: id },
            })
          } else {
            toast(
              <ToasterContent variant="success">
                SSO enabled successfully
              </ToasterContent>,
              { position: 'top-right' }
            )
          }
        } catch {
          toast(
            <ToasterContent variant="error">
              Failed to enable SSO
            </ToasterContent>,
            { position: 'top-right' }
          )
        }
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <H3>Single sign-on</H3>
        </CardTitle>
        <CardAction>
          {isSamlEnabled ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  navigateToPath({ pathname: '/single-sign-on/disable' })
                }
              >
                Disable
              </Button>
              <Button variant="outline" onClick={openSamlSettingsForm}>
                Change
              </Button>
            </div>
          ) : (
            <Button onClick={openSamlSettingsForm} disabled={isPending}>
              {isPending ? <Loader className="animate-spin" /> : null}
              Enable
            </Button>
          )}
        </CardAction>
        <CardDescription>
          Connect your identity provider with Sevalla so users can log in with
          single sign-on (SAML SSO). This simplifies authentication and enhances
          security.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SsoStatusIndicator isEnabled={isSamlEnabled} domain={samlDomain} />
      </CardContent>

      <DisableSsoDialog
        isOpen={isDisableDialogOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/single-sign-on',
            options: { replace: true },
          })
        }
      />
    </Card>
  )
}
